import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Skeleton, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowForward, LocationOn } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { trackEvent, trackHomeImpression, flushHomeImpressions } from '../../services/analyticsService';
import FeaturedCarousel from './FeaturedCarousel';
import ProviderCard, { providerCardBackground } from '../Provider/ProviderCard';
import ProviderMiniRow from './ProviderMiniRow';
import { categoryLandingPath, providersLandingPath } from '../../utils/landingPaths';


const categoryHref = categoryLandingPath;
const countyHref = (county) => providersLandingPath({ county: county.name });

const sectionTitleSx = {
  fontWeight: 700,
  color: 'primary.dark',
  letterSpacing: '-1px',
  fontSize: { xs: '1.9rem', sm: '2.5rem', md: '3rem' },
};

const SectionHeader = ({ id, title, subtitle }) => (
  <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
    <Typography id={id} variant="h3" component="h2" sx={sectionTitleSx}>
      {title}
    </Typography>
    <Box sx={{ width: 60, height: 3, bgcolor: 'primary.main', mx: 'auto', my: 3 }} />
    {subtitle && (
      <Typography variant="h6" component="p" color="text.secondary" sx={{ fontWeight: 300, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Asztalon rács, mobilon vízszintesen görgethető sor (az oldal maga nem görög oldalra)
// inset: asztalon ugyanakkora oldalsó belső margó, mint a Kiemelt partnereink karusszelnél (a léptető
// nyilak helye), így a kártyák ott és itt egyforma szélesek
const CardRow = ({ columns, inset = false, children }) => (
  <Box
    sx={{
      display: { xs: 'flex', md: 'grid' },
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gap: { xs: 2, md: 3 },
      overflowX: { xs: 'auto', md: 'visible' },
      scrollSnapType: { xs: 'x mandatory', md: 'none' },
      pb: { xs: 1.5, md: 0 },
      mx: { xs: -2, md: 0 },
      px: { xs: 2, md: inset ? 7 : 0 },
      '& > *': {
        flex: { xs: '0 0 82%', sm: '0 0 46%', md: 'initial' },
        scrollSnapAlign: 'start',
        minWidth: 0,
      },
    }}
  >
    {children}
  </Box>
);

// A „Szolgáltatók kategóriánként” szekcióban paneleken bemutatott (legnépszerűbb, nem üres) kategóriák száma
const TOP_CATEGORY_COUNT = 3;

const trackCategoryClick = (category, band = 'category') =>
  trackEvent('home_discovery_click', { band, target: category.slug });

// Szám-jelvény a kategória neve mellé
const CountBadge = ({ count }) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      minWidth: 22,
      px: 0.75,
      borderRadius: '999px',
      textAlign: 'center',
      fontSize: '0.72rem',
      fontWeight: 700,
      lineHeight: '20px',
      color: 'primary.dark',
      bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
    }}
  >
    {count}
  </Box>
);

/**
 * Egy népszerű kategória kompakt panele: fejléc (ikon, név, darabszám), legfeljebb 3 kompakt szolgáltató-sor,
 * lábléc-link a kategória landing oldalára.
 */
const CategoryPanel = ({ category }) => (
  <Box
    component="article"
    aria-labelledby={`home-category-${category.slug}`}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      background: (t) => providerCardBackground(t),
      borderRadius: '16px',
      border: '1px solid',
      borderColor: 'divider',
      p: { xs: 1.5, sm: 2 },
      minWidth: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, mb: 1 }}>
      {category.icon && (
        <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: '50%', bgcolor: 'primary.light', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={category.icon} alt="" width={24} height={24} style={{ objectFit: 'contain' }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography id={`home-category-${category.slug}`} component="h3" noWrap sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '1.05rem', lineHeight: 1.25 }}>
          {category.name}
        </Typography>
        <Typography component="p" variant="caption" color="text.secondary">
          {category.providerCount} szolgáltató
        </Typography>
      </Box>
    </Box>

    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, flex: 1 }}>
      {category.providers.map((provider, index) => (
        <li key={provider.id}>
          <ProviderMiniRow provider={provider} listName={`home_category_${category.slug}`} listPosition={index + 1} />
        </li>
      ))}
    </Box>

    <Button
      component={Link}
      to={categoryHref(category)}
      endIcon={<ArrowForward />}
      onClick={() => trackCategoryClick(category)}
      sx={{ mt: 1, alignSelf: 'flex-start', fontWeight: 600, borderRadius: '50px', px: 1 }}
    >
      Összes megtekintése ({category.providerCount})
    </Button>
  </Box>
);

// Kategória- és megye-linkek közös „pill” stílusa
const pillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  maxWidth: '100%',
  pl: 1.5,
  pr: 0.75,
  py: 0.625,
  borderRadius: '999px',
  border: '1px solid',
  borderColor: 'divider',
  background: (t) => providerCardBackground(t),
  color: 'primary.dark',
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1.3,
  textDecoration: 'none',
  transition: 'border-color 0.2s ease, background 0.2s ease',
  // Hover: a `background` rövid forma felülírja a gradienst, így a halvány zöld tónus látszik
  '@media (hover: hover)': { '&:hover': { borderColor: 'primary.main', background: (t) => alpha(t.palette.primary.main, 0.05) } },
  '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
};

/**
 * Minden további kategória: tördelődő „pill” linkek a landing oldalakra, a szolgáltatók számával.
 * Mindig látható, valódi linkek (SEO), nincs vízszintes görgetés, a pill a szöveghez igazodik.
 */
const CategoryPills = ({ categories }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
    {categories.map((category) => (
      <li key={category.id} style={{ maxWidth: '100%' }}>
        <Box
          component={Link}
          to={categoryHref(category)}
          onClick={() => trackCategoryClick(category, 'category_pill')}
          aria-label={`${category.name} – ${category.providerCount} szolgáltató`}
          sx={pillSx}
        >
          <span>{category.name}</span>
          <CountBadge count={category.providerCount} />
        </Box>
      </li>
    ))}
  </Box>
);

// Főoldali megjelenés-mérés a szolgáltatói statisztikához: egy kártya akkor számít megjelenésnek,
// ha legalább félig látszott (a vízszintes görgetésben / Swiperben kilógó kártya nem), és
// oldalmegtekintésenként sávonként egyszer. A sávot a szekció data-impression-band attribútuma adja.
const useHomeImpressionTracking = (rootRef, enabled) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root || typeof IntersectionObserver === 'undefined') return undefined;

    const counted = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        const providerId = Number(entry.target.dataset.providerId);
        const band = entry.target.closest('[data-impression-band]')?.dataset.impressionBand;
        const key = `${providerId}:${band}`;
        if (!providerId || !band || counted.has(key)) return;
        counted.add(key);
        trackHomeImpression(providerId, band);
      });
    }, { threshold: 0.5 });

    const observeCards = () => {
      root.querySelectorAll('[data-impression-band] [data-provider-id]').forEach((el) => io.observe(el));
    };
    observeCards();
    // A Swiper (loop módban) később klónozza a diákat — az új kártyákat is figyeljük
    const mo = new MutationObserver(observeCards);
    mo.observe(root, { childList: true, subtree: true });

    const onVisibilityChange = () => { if (document.visibilityState === 'hidden') flushHomeImpressions(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      io.disconnect();
      mo.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flushHomeImpressions();
    };
  }, [rootRef, enabled]);
};

const HomeDiscoveryBands = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const rootRef = useRef(null);
  useHomeImpressionTracking(rootRef, Boolean(data));

  useEffect(() => {
    let cancelled = false;
    apiService
      .get(API_ENDPOINTS.HOME.DISCOVERY)
      .then((response) => {
        if (!cancelled && response.success) setData(response.data);
      })
      .catch((error) => console.error('Hiba a főoldali ajánlások betöltésekor:', error))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const openProfile = (provider) => navigate(`/szolgaltato/${provider.slug || provider.id}`);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Skeleton variant="text" sx={{ fontSize: '3rem', maxWidth: 420, mx: 'auto', mb: 4 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={420} sx={{ borderRadius: '20px', display: { xs: i > 0 ? 'none' : 'block', md: 'block' } }} />
          ))}
        </Box>
      </Container>
    );
  }

  // Hiba vagy üres válasz: a sávok csendben elmaradnak, a főoldal többi része változatlan
  if (!data) return null;

  const { featuredProviders = [], categories = [], counties = [], newProviders = [] } = data;
  // A backend népszerűség (szolgáltatószám) szerint rendezve adja a kategóriákat
  const topCategories = categories.filter((c) => c.providers.length > 0).slice(0, TOP_CATEGORY_COUNT);
  const topIds = new Set(topCategories.map((c) => c.id));
  // Csak nem üres kategória kerül a listába: üres (noindex) landing oldalra nem linkelünk
  const otherCategories = categories.filter((c) => !topIds.has(c.id) && c.providerCount > 0);

  return (
    <Box ref={rootRef}>
      {/* Kiemelt partnereink: TOP, majd a többi kiemelési szint rotálva */}
      {featuredProviders.length > 0 && (
        <Box
          component="section"
          aria-labelledby="home-featured-title"
          data-impression-band="featured"
          sx={{ py: { xs: 6, md: 10 }, background: 'linear-gradient(180deg, #f4f8f4 0%, #ffffff 100%)' }}
        >
          <Container maxWidth="lg">
            <SectionHeader
              id="home-featured-title"
              title="Kiemelt partnereink"
              subtitle="Ellenőrzött, kiemelkedő minőségű esküvői szolgáltatók – a legjobb párok választása."
            />
            <Box sx={{ mb: 3 }}>
              <FeaturedCarousel providers={featuredProviders} listName="home_featured" />
            </Box>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                component={Link}
                to="/szolgaltatok"
                variant="contained"
                endIcon={<ArrowForward />}
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: '50px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
              >
                Fedezd fel az összes szolgáltatónkat
              </Button>
            </Box>
          </Container>
        </Box>
      )}

      {/* Szolgáltatók kategóriánként: a 3 legnépszerűbb (nem üres) kategória kompakt panelen, 3-3 szolgáltatóval;
          alatta minden további kategória mindig látható linkként, a szolgáltatók számával */}
      {categories.length > 0 && (
        <Box
          component="section"
          aria-labelledby="home-categories-title"
          data-impression-band="category"
          // Telt halvány zöld háttér + felső vonal: a fehérbe fakuló Kiemelt partnereink szekciótól elváljon
          sx={{ py: { xs: 6, md: 9 }, background: '#f4f8f4', borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Container maxWidth="lg">
            <SectionHeader
              id="home-categories-title"
              title="Szolgáltatók kategóriánként"
              subtitle="A legnépszerűbb kategóriák szolgáltatói – és alatta az összes kategória egy kattintásra."
            />
            {topCategories.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
                  gap: { xs: 2, md: 3 },
                  mb: { xs: 4, md: 5 },
                }}
              >
                {topCategories.map((category) => (
                  <CategoryPanel key={category.id} category={category} />
                ))}
              </Box>
            )}
            {otherCategories.length > 0 && (
              <>
                <Typography component="h3" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '1.05rem', mb: 1.5 }}>
                  További kategóriák
                </Typography>
                <CategoryPills categories={otherCategories} />
              </>
            )}
            {counties.length > 0 && (
              <Box sx={{ mt: { xs: 3, md: 4 } }}>
                <Typography id="home-counties-title" component="h3" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '1.05rem', mb: 1.5 }}>
                  Szolgáltatók megyénként
                </Typography>
                <Box component="ul" aria-labelledby="home-counties-title" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {counties.map((county) => (
                    <li key={county.slug} style={{ maxWidth: '100%' }}>
                      <Box
                        component={Link}
                        to={countyHref(county)}
                        onClick={() => trackEvent('home_discovery_click', { band: 'county', target: county.slug })}
                        aria-label={`${county.name} – ${county.providerCount} szolgáltató`}
                        sx={pillSx}
                      >
                        <LocationOn sx={{ fontSize: 16, color: 'primary.main', ml: -0.5 }} />
                        <span>{county.name}</span>
                        <CountBadge count={county.providerCount} />
                      </Box>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* Új a platformon */}
      {newProviders.length > 0 && (
        <Box
          component="section"
          aria-labelledby="home-new-title"
          data-impression-band="new"
          sx={{ py: { xs: 6, md: 10 }, background: '#ffffff' }}
        >
          <Container maxWidth="lg">
            <SectionHeader
              id="home-new-title"
              title="Új a platformon"
              subtitle="Friss arcok a Wedigón – ismerd meg a legújabb szolgáltatóinkat."
            />
            <CardRow columns={4}>
              {newProviders.map((provider, index) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  animationDelay={index * 100}
                  listPosition={index + 1}
                  listName="home_new"
                  categoryHref={categoryHref}
                  onCardClick={openProfile}
                />
              ))}
            </CardRow>
          </Container>
        </Box>
      )}
    </Box>
  );
};

// memo: a Home újrarenderelései (pl. hero láthatóság-váltás) ne rendereljék újra a sok kártyát
export default React.memo(HomeDiscoveryBands);
